/**
 * Planar Drag Gizmo Service
 *
 * Inspector V2 service that manages DragToMove instances when the custom
 * "planar" gizmo mode is active (Y key).
 *
 * Creates a DragToMove for every SceneObject in the scene so that any object
 * can be grabbed directly. On grab, the object is selected in the inspector
 * and the drag begins immediately.
 *
 * Only the currently selected object gets a ground circle (blue XZ disc).
 * Selection highlight is handled by the Inspector's own outline system.
 *
 * Follows the same pattern as bounding-box-gizmo-service.ts:
 * - Watches planar mode state + selected entity
 * - Hooks into the undo system via hookGizmoInstance()
 */

import type { ServiceDefinition, IGizmoService, ISelectionService } from '@babylonjs/inspector';
import { GizmoServiceIdentity, SelectionServiceIdentity } from '@babylonjs/inspector';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { DragToMove } from 'trace-viewer/scene';
import { isPlanarModeActive, onPlanarModeChanged } from './gizmo-keyboard-service';
import { hookGizmoInstance } from './undo-gizmo-service';
import { getUndoManager, isStructuralRoot } from 'trace-viewer/editor';

export const PlanarDragServiceDefinition: ServiceDefinition<[], [IGizmoService, ISelectionService]> = {
    friendlyName: 'Planar Drag Gizmo',
    consumes: [GizmoServiceIdentity, SelectionServiceIdentity],

    factory: (gizmoService: IGizmoService, selectionService: ISelectionService) => {
        /** One DragToMove per SceneObject transform, keyed by TransformNode. */
        const activeDrags = new Map<TransformNode, DragToMove>();

        /** The currently selected entity — only this one gets a ground circle. */
        let selectedEntity: TransformNode | null = null;

        /** XR session active — disable all drag interactions in XR. */
        let inXR = false;

        /** Play mode active — editor drags must not grab runtime content.
         *  In play mode only Moveable/Rotatable objects are draggable, via
         *  their own runtime DragToMove instances. */
        let inPlayMode = false;

        function sync(): void {
            const active = isPlanarModeActive() && !inXR && !inPlayMode;
            const newSelected = selectionService.selectedEntity instanceof TransformNode
                ? selectionService.selectedEntity
                : null;

            if (!active) {
                detachAll();
                selectedEntity = null;
                return;
            }

            // Update ground circle when selection changes
            if (newSelected !== selectedEntity) {
                // Old selection loses ground circle
                if (selectedEntity) {
                    const existing = activeDrags.get(selectedEntity);
                    if (existing) {
                        existing.setOptions({ groundCircle: false });
                    }
                }
                selectedEntity = newSelected;
                // New selection gains ground circle
                if (selectedEntity) {
                    const existing = activeDrags.get(selectedEntity);
                    if (existing) {
                        existing.setOptions({ groundCircle: true });
                    }
                }
            }

            // Ensure all scene objects have DragToMove instances
            ensureAllObjects();
        }

        function ensureAllObjects(): void {
            const traceScene = getUndoManager().traceScene;
            if (!traceScene) return;

            const sceneObjects = traceScene.getSceneObjects();
            const currentTransforms = new Set<TransformNode>();

            for (const so of sceneObjects) {
                // Skip both roots — each is an ancestor of everything below it
                // and would claim every click via _isMeshOfTarget's parent walk
                // (the document root is draggable through the gizmo, not by
                // planar-dragging any mesh in the scene).
                if (isStructuralRoot(so)) continue;

                const transform = so.transform;
                currentTransforms.add(transform);

                if (activeDrags.has(transform)) continue;

                const scene = transform.getScene();
                const isSelected = transform === selectedEntity;
                const dtm = new DragToMove(transform, scene, {
                    groundCircle: isSelected,
                    snap: true,
                    onGrab: () => {
                        // Select this object in the inspector on grab
                        selectionService.selectedEntity = transform;
                    },
                });
                dtm.enabled = true;
                hookGizmoInstance(dtm);
                activeDrags.set(transform, dtm);
            }

            // Remove DragToMove for objects no longer in the scene
            for (const [transform, dtm] of activeDrags) {
                if (!currentTransforms.has(transform)) {
                    dtm.dispose();
                    activeDrags.delete(transform);
                }
            }
        }

        function detachAll(): void {
            for (const dtm of activeDrags.values()) {
                dtm.dispose();
            }
            activeDrags.clear();
        }

        const planarObserver = onPlanarModeChanged.add(sync);
        const selectionObserver = selectionService.onSelectedEntityChanged.add(sync);
        const modeObserver = gizmoService.onGizmoModeChanged.add(sync);

        // XR state: disable planar drags during XR sessions
        const xrHandler = (e: Event) => {
            const detail = (e as CustomEvent<{ isActive: boolean }>).detail;
            inXR = detail.isActive;
            sync();
        };
        // Play mode: tear down editor drags while the scene runs. On `window`
        // rather than the scene's event system (which the XR gate captures
        // once, and which is null when the service predates the scene) — the
        // event bubbles composed off the viewer element, so this can't be
        // missed however late the scene arrives.
        const playModeHandler = (e: Event) => {
            const detail = (e as CustomEvent<{ playMode: boolean }>).detail;
            inPlayMode = detail.playMode;
            sync();
        };

        const traceScene = getUndoManager().traceScene;
        const eventSystem = traceScene?.getEventSystem();
        eventSystem?.addEventListener('trace-viewer:xr-state-changed', xrHandler);
        window.addEventListener('trace-viewer:playmode-changed', playModeHandler);

        // Check current XR state (session may already be active)
        const currentXRState = (eventSystem as unknown as { xrState?: string })?.xrState;
        inXR = currentXRState === 'active';
        inPlayMode = traceScene?.isPlayMode() ?? false;

        // Initial sync
        sync();

        return {
            dispose: () => {
                planarObserver?.remove();
                selectionObserver?.remove();
                modeObserver?.remove();
                eventSystem?.removeEventListener('trace-viewer:xr-state-changed', xrHandler);
                window.removeEventListener('trace-viewer:playmode-changed', playModeHandler);
                detachAll();
            },
        };
    },
};
